import React, { memo, useState, useEffect, useCallback, useMemo } from 'react';
import { Popover, Badge, Button, Spin, Empty, Typography, Tag } from 'antd';
import { PiBellBold, PiCheckCircleBold, PiArrowsClockwiseBold, PiAtBold } from 'react-icons/pi';
import styled from 'styled-components';
import { remote_getMyNotifications } from '~/common/typescript/apis/NotificationAPIs';
import { Notification_Base } from '~/common/typescript/models/Notification';
import { useTranslation } from 'react-i18next';
import { useAtom, useSetAtom } from 'jotai';
import { userInformationAtom } from '~/stores/UserStore';
import { showCommentPanelAtom } from '~/components/Map/stores/mapStore';
import { showCommentOnMapAtom } from '~/components/Map/Comment/CommentStore';
import { useLocation, useNavigate } from 'react-router-dom';

const { Text } = Typography;

const READ_NOTIF_STORAGE_KEY = 'geocloud_read_notification_ids';

const loadReadIds = (): Set<number> => {
  try {
    const raw = localStorage.getItem(READ_NOTIF_STORAGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr.map(Number)) : new Set();
  } catch {
    return new Set();
  }
};

const saveReadIds = (ids: Set<number>) => {
  try {
    localStorage.setItem(READ_NOTIF_STORAGE_KEY, JSON.stringify(Array.from(ids)));
  } catch {
    // ignore storage errors
  }
};

export interface CommentMentionPayload {
  kind?: string;
  type?: string;
  projectId?: number;
  projectName?: string;
  commentId?: number;
  commentTitle?: string;
  geojson?: string;
  authorId?: number;
  authorName?: string;
}

const parseNotificationMetadata = (item: Notification_Base): CommentMentionPayload | null => {
  const raw = item.jsonData || (item as any).data;
  if (!raw) return null;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (parsed && typeof parsed === 'object') {
      return parsed as CommentMentionPayload;
    }
  } catch {
    // ignore invalid json
  }
  return null;
};

interface NotificationBellProps {
  className?: string;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ className }) => {
  const { t } = useTranslation();
  const [userInformation] = useAtom(userInformationAtom);
  const setShowCommentPanel = useSetAtom(showCommentPanelAtom);
  const setShowCommentOnMap = useSetAtom(showCommentOnMapAtom);
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState<Notification_Base[]>([]);
  const [readIds, setReadIds] = useState<Set<number>>(() => loadReadIds());

  const fetchNotifications = useCallback(async () => {
    if (!userInformation) return;
    try {
      setLoading(true);
      const res = await remote_getMyNotifications({ page: 1, pageSize: 15 });
      const items: Notification_Base[] = Array.isArray(res?.results)
        ? res.results
        : Array.isArray(res?.data)
          ? res.data
          : [];
      setNotifications(items);
    } catch (err) {
      console.warn('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [userInformation]);

  useEffect(() => {
    if (userInformation) {
      fetchNotifications();
    }
  }, [userInformation, fetchNotifications]);

  useEffect(() => {
    const handleRefresh = () => {
      fetchNotifications();
    };
    window.addEventListener('geocloud:refresh-notifications', handleRefresh);
    return () => {
      window.removeEventListener('geocloud:refresh-notifications', handleRefresh);
    };
  }, [fetchNotifications]);

  const unreadCount = useMemo(() => {
    return notifications.filter((item) => item.id && !readIds.has(item.id)).length;
  }, [notifications, readIds]);

  if (!userInformation) {
    return null;
  }

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen) {
      fetchNotifications();
    }
  };

  const handleMarkAllRead = () => {
    const next = new Set(readIds);
    notifications.forEach((n) => {
      if (n.id) next.add(n.id);
    });
    setReadIds(next);
    saveReadIds(next);
  };

  const handleClickNotification = (item: Notification_Base) => {
    if (item.id) {
      const next = new Set(readIds);
      next.add(item.id);
      setReadIds(next);
      saveReadIds(next);
    }

    const meta = parseNotificationMetadata(item);
    if (meta && (meta.kind === 'COMMENT_MENTION' || meta.type === 'COMMENT_MENTION' || meta.commentId)) {
      setOpen(false);
      setShowCommentOnMap(true);
      setShowCommentPanel(true);

      const targetPath = meta.projectId ? `/project/${meta.projectId}` : null;
      if (targetPath && !location.pathname.startsWith(targetPath)) {
        navigate(`${targetPath}?commentId=${meta.commentId || ''}`);
      }

      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent('geocloud:focus-comment', {
            detail: {
              commentId: meta.commentId,
              projectId: meta.projectId,
              geojson: meta.geojson,
            },
          }),
        );
      }, 120);
    }
  };

  const formatTime = (dateStr?: string) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('vi-VN', {
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const popoverContent = (
    <UI_PopoverContainer data-testid="notification-popover">
      <UI_PopoverHeader>
        <UI_HeaderTitle>
          <PiBellBold style={{ fontSize: 16, color: 'var(--color-primary)' }} />
          <span>{t('notification') || 'Thông báo'}</span>
          {unreadCount > 0 && (
            <Tag color="blue" style={{ marginLeft: 4, fontSize: 11, lineHeight: '16px', padding: '0 6px' }}>
              {unreadCount} mới
            </Tag>
          )}
        </UI_HeaderTitle>
        <UI_HeaderActions>
          {unreadCount > 0 && (
            <Button
              type="link"
              size="small"
              onClick={handleMarkAllRead}
              style={{ fontSize: 11.5, padding: '0 4px' }}
              data-testid="notification-mark-all-read-btn"
            >
              Đã đọc tất cả
            </Button>
          )}
          <Button
            type="text"
            size="small"
            icon={<PiArrowsClockwiseBold />}
            loading={loading}
            onClick={fetchNotifications}
            title="Làm mới"
            data-testid="notification-refresh-btn"
          />
        </UI_HeaderActions>
      </UI_PopoverHeader>

      <UI_PopoverBody>
        {loading && notifications.length === 0 ? (
          <UI_CenterBox>
            <Spin size="small" />
          </UI_CenterBox>
        ) : notifications.length === 0 ? (
          <UI_CenterBox>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('no_notification') || 'Không có thông báo mới'}
            />
          </UI_CenterBox>
        ) : (
          <UI_NotificationList>
            {notifications.map((item, index) => {
              const meta = parseNotificationMetadata(item);
              const isMention =
                meta?.kind === 'COMMENT_MENTION' ||
                meta?.type === 'COMMENT_MENTION' ||
                Boolean(meta?.commentId);
              const isUnread = item.id ? !readIds.has(item.id) : false;

              return (
                <UI_NotificationItem
                  key={item.id || index}
                  $unread={isUnread}
                  data-testid={`notification-item-${item.id || index}`}
                  onClick={() => handleClickNotification(item)}
                >
                  <UI_ItemIcon $mention={isMention}>
                    {isMention ? <PiAtBold /> : <PiCheckCircleBold />}
                  </UI_ItemIcon>
                  <UI_ItemContent>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                      {isMention && (
                        <Tag
                          color="geekblue"
                          style={{ margin: 0, fontSize: 10, lineHeight: '15px', padding: '0 5px' }}
                        >
                          @Nhắc đến bạn
                        </Tag>
                      )}
                      <UI_ItemTitle ellipsis={{ tooltip: item.title }} style={{ marginBottom: 0 }}>
                        {item.title || 'Thông báo hệ thống'}
                      </UI_ItemTitle>
                    </div>
                    <UI_ItemText>{item.content || ''}</UI_ItemText>
                    {item.createdAt && <UI_ItemTime>{formatTime(item.createdAt)}</UI_ItemTime>}
                  </UI_ItemContent>
                </UI_NotificationItem>
              );
            })}
          </UI_NotificationList>
        )}
      </UI_PopoverBody>
    </UI_PopoverContainer>
  );

  return (
    <Popover
      content={popoverContent}
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
      placement="bottomRight"
      overlayClassName="notification-bell-popover"
      overlayInnerStyle={{ padding: 0, borderRadius: 8, overflow: 'hidden' }}
    >
      <UI_BellButton className={className} title="Thông báo" data-testid="notification-bell-btn">
        <Badge count={unreadCount} size="small" offset={[2, -2]}>
          <PiBellBold style={{ fontSize: 18 }} />
        </Badge>
      </UI_BellButton>
    </Popover>
  );
};

export default memo(NotificationBell);

const UI_BellButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  cursor: pointer;
  color: #475569;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(59, 130, 246, 0.08);
    color: var(--color-primary);
  }
`;

const UI_PopoverContainer = styled.div`
  width: 340px;
  max-width: 90vw;
  background: #ffffff;
  display: flex;
  flex-direction: column;
`;

const UI_PopoverHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-bottom: 1px solid #f1f5f9;
  background: #fafafa;
`;

const UI_HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  color: #1e293b;
`;

const UI_HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const UI_PopoverBody = styled.div`
  max-height: 360px;
  overflow-y: auto;
`;

const UI_CenterBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 28px 16px;
`;

const UI_NotificationList = styled.div`
  display: flex;
  flex-direction: column;
`;

const UI_NotificationItem = styled.div<{ $unread?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 14px;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  background: ${(props) => (props.$unread ? '#f8fbff' : '#ffffff')};
  transition: background 0.15s ease;

  &:hover {
    background: #eff6ff;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const UI_ItemIcon = styled.div<{ $mention?: boolean }>`
  color: ${(props) => (props.$mention ? '#2563eb' : 'var(--color-primary)')};
  background: ${(props) => (props.$mention ? '#dbeafe' : 'transparent')};
  width: ${(props) => (props.$mention ? '26px' : 'auto')};
  height: ${(props) => (props.$mention ? '26px' : 'auto')};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  margin-top: 2px;
  flex-shrink: 0;
`;

const UI_ItemContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const UI_ItemTitle = styled(Text)`
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 2px;
`;

const UI_ItemText = styled.div`
  font-size: 12px;
  color: #475569;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const UI_ItemTime = styled.span`
  font-size: 11px;
  color: #94a3b8;
  margin-top: 4px;
`;
